import React, { useEffect, useMemo, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { getBaseURL, pxToRem, setDataToClipboard } from "@/util/common";
import FixedNavBar from "@/components/fixedNavBar";
import { Button, Grid, Image } from "antd-mobile";
import useCustomTranslation from "@/hooks/useTranslation";
import API from "@/api";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/redux/store";
import { showGlobalLoading } from "@/components/globalLoading";
import DropTime from "@/components/dropTime";
import RecommendList from "@/components/recommendList";
import useRouter from "@/hooks/useRouter";
import { RecommendItem } from "@/api/type";
import { getUserInfoMethod } from "@/redux/reducers/authSlice";
import { has } from "lodash";
import IncomePopup from "@/components/income";
import { isDef } from "@/util/validate";

const CashbackManage: React.FC = () => {
	const classes = useStyles();
	const { t } = useCustomTranslation();
	const dispatch: AppDispatch = useDispatch();
	const { dataList: appConfig } = useSelector(
		(state: RootState) => state.config
	);
	const { customNavigate } = useRouter();
	const { showLoading, hideLoading } = showGlobalLoading();
	const { userInfo } = useSelector((state: RootState) => state.auth);
	const [recommendData, setRecommendData] = useState<RecommendItem[]>([]);
	const [hasMore, setHasMore] = useState(true);
	const [stats, setStats] = useState({
		unSettleCoin: 0, //未结算
		members: 0, //注册用户数
		rechargeMembers: 0, //首充用户数
		buyMembers: 0, //投注用户数
		buyCoin: 0, //投注额
	});
	const [incomePopupVisible, setIncomePopupVisible] = useState(false);
	const inviteLink = useMemo(() => {
		const baseUrl = isDef(appConfig?.home.share)
			? appConfig?.home.share
			: getBaseURL();

		return (
			baseUrl +
			"/#/?i=" +
			userInfo?.inviteCode +
			"&c=" +
			userInfo?.channelCode
		);
	}, [userInfo, appConfig]);

	// 获取代理首页数据
	const getFinanceAgentHomeData = async () => {
		try {
			showLoading();
			const result: any = await API.finances.apiFinanceAgentCommission({
				day: -daysToSubtract.current,
			});
			if (result?.code === 0) {
				const data = result.content.data;
				setStats({
					unSettleCoin: data.unSettleCoin || 0, //未结算
					members: data.members || 0, //注册用户数
					rechargeMembers: data.rechargeMembers || 0, //首充用户数
					buyMembers: data.buyMembers || 0, //投注用户数
					buyCoin: data.buyCoin || 0, //投注额
				});
				setRecommendData(data.recordList || []);
			}
		} catch (error) {
			console.log("获取代理首页数据失败:", error);
		} finally {
			hideLoading();
		}
	};

	// 复制邀请链接
	const handleCopy = async () => {
		setDataToClipboard(inviteLink, t("CopySuccess"));
	};

	const daysToSubtract = useRef<number>(30);
	const handleOptionClick = (newIndex: number) => {
		daysToSubtract.current = newIndex;
		getFinanceAgentHomeData();
	};

	// 处理加载更多
	const handleLoadMore = async (isRetry: boolean) => {
		// 这里实现加载更多逻辑
		setHasMore(false);
	};

	const goToPageHandle = () => {
		// 有返水
		if (has(userInfo?.balance, "3") && (userInfo?.balance["3"] ?? 0) > 0) {
			setIncomePopupVisible(true);
		} else {
			customNavigate("/cashbackRecord");
		}
	};

	useEffect(() => {
		const onInit = async () => {
			await dispatch(getUserInfoMethod());
			getFinanceAgentHomeData();
		};
		onInit();
	}, []);

	return (
		<div className="router-main">
			<FixedNavBar title={t("RebateManagement")} />
			<div className={`${classes.container} router-view`}>
				<div className={classes.cardWrap}>
					<div className={classes.cardContent}>
						<div className={classes.title}>
							<Image
								src={require("@/assets/images/icons/coins-stacked-02.png")}
								className="icon"
							/>
							<span>{t("AvailableCashback")}</span>
						</div>
						<div className={classes.amount}>
							<Image
								src={require("@/assets/images/icons/USDT.png")}
								width={24}
								height={24}
							/>
							<span className={classes.value}>
								{userInfo?.balance["3"] ?? 0}
							</span>
						</div>
						{stats.unSettleCoin > 0 && (
							<div className={classes.desc}>
								{stats.unSettleCoin} USDT {t("PendingCashback")}
							</div>
						)}
					</div>
					<Button className={classes.button} onClick={goToPageHandle}>
						{t("Collect")}
					</Button>
				</div>

				{isDef(userInfo?.inviteCode) && (
					<>
						<div className={classes.columnTitle}>
							<div className="l">
								<Image
									src={require("@/assets/images/icons/pie-chart.png")}
									className="icon"
								/>
								<strong>{t("Count")}</strong>
							</div>
							<DropTime
								key="dropTime"
								index={daysToSubtract.current}
								handleOptionClick={handleOptionClick}
							/>
						</div>
						<div className={classes.countWrap}>
							<Grid columns={2} gap={8}>
								<Grid.Item>
									<dl className={classes.cardMonomer}>
										<dt>
											<Image
												src={require("@/assets/images/icons/coins-stacked.png")}
												className="icon"
											/>
											<span>
												{t("RegisterUserCount")}
											</span>
										</dt>
										<dd className="u">{stats.members}</dd>
									</dl>
								</Grid.Item>
								<Grid.Item>
									<dl
										onClick={() => {}}
										className={classes.cardMonomer}
									>
										<dt>
											<Image
												src={require("@/assets/images/icons/coins-stacked.png")}
												className="icon"
											/>
											<span>{t("TotalBetAmount")}</span>
										</dt>
										<dd className="u">
											{stats.buyCoin} USDT
										</dd>
									</dl>
								</Grid.Item>
								<Grid.Item>
									<dl
										onClick={() => {}}
										className={classes.cardMonomer}
									>
										<dt>
											<Image
												src={require("@/assets/images/icons/coins-stacked.png")}
												className="icon"
											/>
											<span>
												{t("FirstChargeUserCount")}
											</span>
										</dt>
										<dd className="u">
											{stats.rechargeMembers}
										</dd>
									</dl>
								</Grid.Item>
								<Grid.Item>
									<dl
										onClick={() => {}}
										className={classes.cardMonomer}
									>
										<dt>
											<Image
												src={require("@/assets/images/icons/coins-stacked.png")}
												className="icon"
											/>
											<span>{t("BetUserCount")}</span>
										</dt>
										<dd className="u">
											{stats.buyMembers}
										</dd>
									</dl>
								</Grid.Item>
							</Grid>
						</div>
					</>
				)}

				<div className={classes.columnTitle}>
					<div className="l">
						<Image
							src={require("@/assets/images/icons/coins-hand.png")}
							className="icon"
						/>
						<strong>{t("RebateRatio")}</strong>
					</div>
					{isDef(userInfo?.inviteCode) && (
						<div
							onClick={() => customNavigate("/lowerMembers")}
							className="more"
						>
							{t("SubordinateRebateRatio")}
						</div>
					)}
				</div>

				<div className={classes.rebateSection}>
					<div className={classes.rebateTitle}>
						<span>{t("YourCashback")}</span>
						<div className={classes.rebateValue}>
							{userInfo?.rolling ? userInfo?.rolling : 0}%
						</div>
					</div>
					<div className={classes.rebateSlider}>
						<Image
							src={require("@/assets/images/icons/slider_l.png")}
							className="icon"
						/>
						<Image
							src={require("@/assets/images/icons/slider_o.png")}
							className="icon"
						/>
						<Image
							src={require("@/assets/images/icons/slider_l.png")}
							className="icon"
						/>
						<Image
							src={require("@/assets/images/icons/slider_o.png")}
							className="icon"
						/>
					</div>
				</div>

				{isDef(userInfo?.inviteCode) && (
					<>
						<div className={classes.columnTitle}>
							<div className="l">
								<Image
									src={require("@/assets/images/icons/discovery.png")}
									className="icon"
								/>
								<strong>{t("MyPromotionTool")}</strong>
							</div>
						</div>

						<div className={classes.promoSection}>
							<div className={classes.inviteCode}>
								<div className={classes.codeLabel}>
									{t("InviteCode")}
								</div>
								<div
									onClick={() =>
										setDataToClipboard(
											userInfo?.inviteCode ?? "",
											t("CopySuccess")
										)
									}
									className={classes.codeValue}
								>
									{userInfo?.inviteCode ?? "-"}
								</div>
							</div>
							<div className={classes.inviteLink}>
								<div className={classes.linkLabel}>
									{t("InviteLink")}
								</div>
								<div className={classes.linkValue}>
									<p>{inviteLink}</p>
									<div onClick={handleCopy} className="copy">
										<Image
											src={require("@/assets/images/icons/copy.png")}
											className="icon"
										/>
										<span>{t("Copy")}</span>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
				<div className={classes.columnTitle}>
					<div className="l">
						<Image
							src={require("@/assets/images/icons/rec.png")}
							className="icon"
						/>
						<strong>{t("MyRecommend")}</strong>
					</div>
					<div
						onClick={() => customNavigate("/recommend")}
						className="more"
					>
						{t("More")}
					</div>
				</div>

				<div
					style={{
						border: "1px solid #353F63",
						borderRadius: pxToRem(8),
						overflow: "hidden",
					}}
				>
					<RecommendList
						data={recommendData}
						hasMore={hasMore}
						onRefresh={getFinanceAgentHomeData}
						onLoadMore={handleLoadMore}
						isPullToRefresh={false}
						isInside={false}
					/>
				</div>
			</div>
			<IncomePopup
				income={userInfo?.balance["3"] ?? 0}
				visible={incomePopupVisible}
				onClose={() => setIncomePopupVisible(false)}
			/>
		</div>
	);
};

const useStyles = createUseStyles({
	container: {
		padding: pxToRem(16),
	},
	cardWrap: {
		display: "flex",
		alignItems: "center",
		borderRadius: pxToRem(10),
		padding: {
			top: pxToRem(14),
			left: pxToRem(14),
			right: pxToRem(18),
			bottom: pxToRem(8),
		},
		marginBottom: pxToRem(15),
		background: "linear-gradient(90deg, #147639 0%, #1E2846 100%)",
	},
	cardContent: {
		display: "flex",
		flexDirection: "column",
		flex: 1,
		justifyContent: "space-between",
	},
	title: {
		display: "flex",
		alignItems: "center",
		marginBottom: pxToRem(4),
		gap: pxToRem(6),
		"& .icon": {
			width: pxToRem(20),
			height: pxToRem(20),
		},
		"& > span": {
			color: "#8BFF78",
			fontFamily: "Noto Sans SC",
			fontWeight: 400,
			fontSize: pxToRem(12),
			lineHeight: 1,
		},
	},
	amount: {
		display: "flex",
		alignItems: "center",
		gap: pxToRem(8),
		marginBottom: pxToRem(2),
	},
	value: {
		color: "#fff",
		fontFamily: "Inter",
		fontWeight: 700,
		fontSize: pxToRem(18),
		lineHeight: pxToRem(34),
	},
	desc: {
		color: "#868FA8",
		marginBottom: pxToRem(16),
		fontFamily: "Noto Sans SC",
		fontWeight: 400,
		fontSize: pxToRem(12),
		lineHeight: 1,
	},
	button: {
		minWidth: pxToRem(112),
		height: pxToRem(40),
		borderRadius: pxToRem(40),
		color: "#fff",
		fontFamily: "Noto Sans SC",
		fontWeight: 500,
		fontSize: pxToRem(14),
		background: "#46D030",
		"--border-style": "none",
	},
	columnTitle: {
		display: "flex",
		justifyContent: "space-between",
		padding: {
			top: pxToRem(12),
			bottom: pxToRem(12),
		},
		"& .l": {
			flex: 1,
			overflow: "hidden",
			display: "flex",
			alignItems: "center",
			"& .icon": {
				width: pxToRem(24),
				height: pxToRem(24),
				marginRight: pxToRem(8),
			},
			"& strong": {
				color: "#FFFFFF",
				fontFamily: "Noto Sans SC",
				fontSize: pxToRem(14),
				fontWeight: 500,
			},
		},
		"& .more": {
			color: "#868FA8",
			fontSize: pxToRem(12),
			fontWeight: 400,
			fontFamily: "Noto Sans SC",
			lineHeight: 1,
		},
	},
	countWrap: {
		marginBottom: pxToRem(10),
	},
	cardMonomer: {
		height: "100%",
		padding: {
			top: pxToRem(15),
			right: pxToRem(10),
			bottom: pxToRem(10),
			left: pxToRem(16),
		},
		borderRadius: pxToRem(10),
		backgroundColor: "#181A20",
		"&.gradient": {
			background: "linear-gradient(90deg, #147639 0%, #1E2846 100%)",
			"& dt": {
				color: "#FFFFFF",
			},
		},
		"&.cashback": {
			position: "relative",
			paddingLeft: pxToRem(105),
			"&:after": {
				position: "absolute",
				content: '""',
				left: pxToRem(9),
				top: pxToRem(3),
				width: pxToRem(73),
				height: pxToRem(76),
				backgroundImage: `url(${require("@/assets/images/icons/cashback.png")})`,
				backgroundRepeat: "no-repeat",
				backgroundSize: "cover",
			},
		},
		"& dt": {
			display: "flex",
			alignItems: "center",
			color: "#868FA8",
			fontSize: pxToRem(12),
			fontWeight: 400,
			"& .icon": {
				width: pxToRem(20),
				height: pxToRem(20),
				marginRight: pxToRem(4),
			},
		},
		"& dd": {
			color: "#FFFFFF",
			fontSize: pxToRem(14),
			fontWeight: 500,
			lineHeight: pxToRem(34),
			"&.u": {
				fontSize: pxToRem(18),
				fontWeight: 700,
				wordBreak: "break-word",
				whiteSpace: "normal",
			},
		},
	},
	rebateSection: {
		position: "relative",
		zIndex: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: pxToRem(26),
		borderRadius: pxToRem(8),
		padding: pxToRem(16),
		marginBottom: pxToRem(16),
		background: "linear-gradient(90deg, #147639 0%, #1E2846 100%)",
	},
	rebateTitle: {
		display: "flex",
		flexDirection: "column",
		gap: pxToRem(8),
		marginBottom: pxToRem(8),
		color: "#fff",
		fontSize: pxToRem(14),
		"& > span": {
			fontFamily: "Noto Sans SC",
			fontWeight: 400,
			fontSize: pxToRem(12),
			lineHeight: pxToRem(18),
		},
	},
	rebateValue: {
		color: "#8BFF78",
		fontFamily: "Inter",
		fontWeight: 700,
		fontSize: pxToRem(20),
	},
	rebateSlider: {
		display: "flex",
		alignItems: "center",
		gap: pxToRem(4),
		fontSize: pxToRem(12),
		color: "#8F95A1",
		"& .icon": {
			width: pxToRem(20),
			height: pxToRem(24),
			marginRight: pxToRem(-10),
			opacity: 0.3,
			animation: "$slideSync 2s ease-in-out infinite",
		},
	},
	"@keyframes slideSync": {
		"0%": {
			transform: "translateX(0)",
			opacity: 0.3,
		},
		"50%": {
			transform: "translateX(8px)",
			opacity: 1,
		},
		"100%": {
			transform: "translateX(0)",
			opacity: 0.3,
		},
	},
	promoSection: {},
	inviteCode: {
		marginBottom: pxToRem(8),
	},
	codeLabel: {
		color: "#868FA8",
		fontSize: pxToRem(12),
		fontFamily: "Noto Sans SC",
		fontWeight: 400,
		marginBottom: pxToRem(5),
	},
	codeValue: {
		color: "#8BFF78",
		fontFamily: "Inter",
		fontWeight: 700,
		fontSize: pxToRem(20),
		lineHeight: pxToRem(34),
	},
	inviteLink: {},
	linkLabel: {
		color: "#868FA8",
		fontSize: pxToRem(12),
		fontFamily: "Noto Sans SC",
		fontWeight: 400,
		marginBottom: pxToRem(10),
	},
	linkValue: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: pxToRem(8),
		fontSize: pxToRem(14),
		color: "#fff",
		minHeight: pxToRem(68),
		borderRadius: pxToRem(10),
		padding: pxToRem(10),
		border: "1px solid #4C66C7",
		"& .copy": {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			minWidth: pxToRem(52),
			minHeight: pxToRem(52),
			borderRadius: pxToRem(5),
			padding: {
				left: pxToRem(12),
				right: pxToRem(12),
			},
			backgroundColor: "#242E4C",
			"&:active": {
				opacity: 0.8,
			},
			"& .icon": {
				width: pxToRem(24),
				height: pxToRem(24),
			},
			"& > span": {
				color: "#868FA8",
				fontFamily: "Poppins",
				fontWeight: 500,
				fontSize: pxToRem(10),
				transform: "scale(0.8)",
			},
		},
	},
});

export default CashbackManage;
